import {Button, Checkbox, Group, Select} from '@mantine/core'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {ConfirmDialog} from 'src/ui/confirm-dialog'

import {formatPrice} from '../../lib/format-price'
import type {ThirdPartyRentalAssetOffering} from './schemas'
import {ThirdPartyRentalAssetOfferingFormModal} from './third-party-rental-asset-offering-form-modal'
import {useRentalAssetTypes} from './use-rental-asset-types'
import {
    useArchiveThirdPartyRentalAssetOffering,
    useCreateThirdPartyRentalAssetOffering,
    useThirdPartyRentalAssetOfferings,
    useUnarchiveThirdPartyRentalAssetOffering,
    useUpdateThirdPartyRentalAssetOffering,
} from './use-third-party-rental-asset-offerings'
import {useThirdPartyRentalCompanies} from './use-third-party-rental-companies'

function hasActiveFilters(companyId: string | null, rentalAssetTypeId: string | null) {
    return companyId != null || rentalAssetTypeId != null
}

function filterOfferings(
    offerings: ThirdPartyRentalAssetOffering[],
    companyId: string | null,
    rentalAssetTypeId: string | null,
) {
    return offerings.filter((offering) => {
        if (companyId != null && offering.companyId !== companyId) {
            return false
        }
        if (rentalAssetTypeId != null && offering.rentalAssetTypeId !== rentalAssetTypeId) {
            return false
        }
        return true
    })
}

function compareOfferings(
    left: ThirdPartyRentalAssetOffering,
    right: ThirdPartyRentalAssetOffering,
    columnAccessor: DataTableSortStatus<ThirdPartyRentalAssetOffering>['columnAccessor'],
) {
    if (columnAccessor === 'defaultDailyRate') {
        const leftValue = left.defaultDailyRate ?? Number.NEGATIVE_INFINITY
        const rightValue = right.defaultDailyRate ?? Number.NEGATIVE_INFINITY
        return leftValue - rightValue
    }
    if (columnAccessor === 'archivedAt') {
        return Number(left.archivedAt != null) - Number(right.archivedAt != null)
    }

    const leftValue = String(left[columnAccessor as keyof ThirdPartyRentalAssetOffering] ?? '')
    const rightValue = String(right[columnAccessor as keyof ThirdPartyRentalAssetOffering] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortOfferings(
    offerings: ThirdPartyRentalAssetOffering[],
    sortStatus: DataTableSortStatus<ThirdPartyRentalAssetOffering>,
) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...offerings].sort(
        (left, right) =>
            directionMultiplier * compareOfferings(left, right, sortStatus.columnAccessor),
    )
}

function formatDailyRate(rate: number | null) {
    if (rate == null) {
        return '—'
    }
    return formatPrice(rate)
}

export function ThirdPartyRentalAssetOfferingsPage() {
    const [companyFilterId, setCompanyFilterId] = useState<string | null>(null)
    const [assetTypeFilterId, setAssetTypeFilterId] = useState<string | null>(null)
    const [includeArchived, setIncludeArchived] = useState(false)
    const {data: offerings = [], isLoading: offeringsLoading} = useThirdPartyRentalAssetOfferings({
        includeArchived,
    })
    const {data: rentalAssetTypes = [], isLoading: typesLoading} = useRentalAssetTypes()
    const {data: companies = [], isLoading: companiesLoading} = useThirdPartyRentalCompanies()
    const createOffering = useCreateThirdPartyRentalAssetOffering()
    const updateOffering = useUpdateThirdPartyRentalAssetOffering()
    const archiveOffering = useArchiveThirdPartyRentalAssetOffering()
    const unarchiveOffering = useUnarchiveThirdPartyRentalAssetOffering()
    const [sortStatus, setSortStatus] = useState<
        DataTableSortStatus<ThirdPartyRentalAssetOffering>
    >({
        columnAccessor: 'companyName',
        direction: 'asc',
    })
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [editingOffering, setEditingOffering] = useState<ThirdPartyRentalAssetOffering | null>(
        null,
    )
    const [archiveTarget, setArchiveTarget] = useState<ThirdPartyRentalAssetOffering | null>(null)

    const isLoading = offeringsLoading || typesLoading || companiesLoading
    const filtersActive = hasActiveFilters(companyFilterId, assetTypeFilterId)

    const companyOptions = useMemo(
        () => companies.map((company) => ({value: company.id, label: company.name})),
        [companies],
    )

    const rentalAssetTypeOptions = useMemo(
        () => rentalAssetTypes.map((type) => ({value: type.id, label: type.name})),
        [rentalAssetTypes],
    )

    const sortedOfferings = useMemo(
        () =>
            sortOfferings(
                filterOfferings(offerings, companyFilterId, assetTypeFilterId),
                sortStatus,
            ),
        [offerings, companyFilterId, assetTypeFilterId, sortStatus],
    )

    function handleOfferingSaved() {
        setCompanyFilterId(null)
        setAssetTypeFilterId(null)
    }

    async function handleArchiveConfirm() {
        if (archiveTarget) {
            try {
                await archiveOffering.mutateAsync(archiveTarget.id)
            } finally {
                setArchiveTarget(null)
            }
        }
    }

    async function handleUnarchive(offering: ThirdPartyRentalAssetOffering) {
        await unarchiveOffering.mutateAsync(offering.id)
    }

    const columns = useMemo((): DataTableColumn<ThirdPartyRentalAssetOffering>[] => {
        return [
            {
                accessor: 'companyName',
                title: 'Company',
                sortable: true,
                filter: (
                    <Select
                        label="Company"
                        description="Show offerings for the selected company"
                        placeholder="All companies"
                        data={companyOptions}
                        value={companyFilterId}
                        onChange={setCompanyFilterId}
                        clearable
                        searchable
                    />
                ),
                filtering: companyFilterId != null,
            },
            {
                accessor: 'rentalAssetTypeName',
                title: 'Asset type',
                sortable: true,
                filter: (
                    <Select
                        label="Asset type"
                        description="Show offerings for the selected asset type"
                        placeholder="All asset types"
                        data={rentalAssetTypeOptions}
                        value={assetTypeFilterId}
                        onChange={setAssetTypeFilterId}
                        clearable
                        searchable
                    />
                ),
                filtering: assetTypeFilterId != null,
            },
            {
                accessor: 'defaultDailyRate',
                title: 'Default daily rate',
                sortable: true,
                render: (row) => formatDailyRate(row.defaultDailyRate),
            },
            {
                accessor: 'archivedAt',
                title: 'Status',
                sortable: true,
                render: (row) => (row.archivedAt == null ? 'Active' : 'Archived'),
            },
            {
                accessor: 'id',
                title: 'Actions',
                width: 220,
                render: (row) => (
                    <Group gap="xs">
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setEditingOffering(row)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="subtle"
                            color={row.archivedAt ? undefined : 'red'}
                            size="compact-sm"
                            onClick={() =>
                                row.archivedAt == null
                                    ? setArchiveTarget(row)
                                    : void handleUnarchive(row)
                            }
                        >
                            {row.archivedAt == null ? 'Archive' : 'Unarchive'}
                        </Button>
                    </Group>
                ),
            },
        ]
    }, [assetTypeFilterId, companyFilterId, companyOptions, rentalAssetTypeOptions])

    return (
        <>
            <Group justify="space-between" mb="md">
                <Checkbox
                    label="Include archived"
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.currentTarget.checked)}
                />
                <Button onClick={() => setCreateModalOpen(true)}>Create offering</Button>
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                idAccessor="id"
                records={sortedOfferings}
                columns={columns}
                fetching={isLoading}
                minHeight={sortedOfferings.length === 0 ? 180 : undefined}
                noRecordsText={
                    filtersActive
                        ? 'No offerings match your filters.'
                        : 'No third-party rental offerings yet.'
                }
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
            />

            <ThirdPartyRentalAssetOfferingFormModal
                opened={createModalOpen}
                offering={null}
                companies={companies}
                rentalAssetTypes={rentalAssetTypes}
                onClose={() => setCreateModalOpen(false)}
                onCreate={(input) => createOffering.mutateAsync(input)}
                onUpdate={(id, input) => updateOffering.mutateAsync({id, input})}
                onSaved={() => {
                    setCreateModalOpen(false)
                    handleOfferingSaved()
                }}
            />

            <ThirdPartyRentalAssetOfferingFormModal
                opened={editingOffering != null}
                offering={editingOffering}
                companies={companies}
                rentalAssetTypes={rentalAssetTypes}
                onClose={() => setEditingOffering(null)}
                onCreate={(input) => createOffering.mutateAsync(input)}
                onUpdate={(id, input) => updateOffering.mutateAsync({id, input})}
                onSaved={() => {
                    setEditingOffering(null)
                    handleOfferingSaved()
                }}
            />

            <ConfirmDialog
                opened={archiveTarget != null}
                title="Archive offering"
                message={`Are you sure you want to archive the offering from ${archiveTarget?.companyName ?? 'this company'}?`}
                confirmLabel="Archive"
                loading={archiveOffering.isPending}
                onConfirm={() => void handleArchiveConfirm()}
                onCancel={() => setArchiveTarget(null)}
            />
        </>
    )
}
